import { supabase } from './supabase';

/**
 * Recalculates and persists current_balance for a given account.
 *
 * RULE: Only transactions with status 'pago' (despesas pagas) or 'recebido'
 * (receitas recebidas) are counted as REALIZED and affect the real balance.
 *
 * Pendente / vencido / atrasado / future transactions are NEVER counted
 * toward current_balance — they belong in projections only.
 *
 * Formula: initial_balance + SUM(receita WHERE status='recebido') - SUM(despesa WHERE status='pago')
 */
export const syncAccountBalance = async (accountId: string | null): Promise<void> => {
  if (!accountId) return;

  // Guard: skip if there's no active session to avoid token refresh errors
  const { data: sessionData } = await supabase.auth.getSession();
  if (!sessionData?.session) return;

  const { data: account } = await supabase
    .from('accounts')
    .select('initial_balance')
    .eq('id', accountId)
    .maybeSingle();

  if (!account) return;

  // ── Only count REALIZED transactions ──────────────────────────────────────
  // 'recebido' = income actually received
  // 'pago'     = expense actually paid
  // All other statuses (pendente, vencido, atrasado) are excluded on purpose.
  const { data: txs } = await supabase
    .from('transactions')
    .select('type, amount, status')
    .eq('account_id', accountId)
    .in('status', ['pago', 'recebido'])
    .is('deleted_at', null); // ← CRÍTICO: excluir transações soft-deleted do cálculo

  const rows = (txs ?? []) as { type: string; amount: number; status: string }[];

  const totalIncome = rows
    .filter(t => (t.type === 'receita' || t.type === 'income') && t.status === 'recebido')
    .reduce((s, t) => s + Number(t.amount), 0);

  const totalExpense = rows
    .filter(t => (t.type === 'despesa' || t.type === 'expense') && t.status === 'pago')
    .reduce((s, t) => s + Number(t.amount), 0);

  const newBalance = Number(account.initial_balance ?? 0) + totalIncome - totalExpense;

  await supabase
    .from('accounts')
    .update({ current_balance: newBalance, updated_at: new Date().toISOString() })
    .eq('id', accountId);
};

/**
 * Recalculates current_balance for ALL accounts belonging to a user.
 * Useful as a safety net after bulk operations (imports, mass deletes, etc.).
 * NOTE: The DB trigger `trg_sync_account_balance` handles per-row changes
 * automatically — this function is a manual override / recovery tool.
 */
export const syncAllAccountBalances = async (userId: string): Promise<void> => {
  // Guard: skip if there's no active session
  const { data: sessionData } = await supabase.auth.getSession();
  if (!sessionData?.session) return;

  const { data: userAccounts } = await supabase
    .from('accounts')
    .select('id')
    .eq('user_id', userId);

  if (!userAccounts?.length) return;

  await Promise.all(
    userAccounts.map((a: { id: string }) => syncAccountBalance(a.id))
  );
};
